import React, { useState, useCallback, useEffect } from 'react';
import { Box, Button, Input, useToast, Text } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { saveAs } from 'file-saver';
import { useAdvances } from '../../../Context/AdvancesContext'

const VendorInvoicePaginatedData = ({
	data,
	setData,
	currentPage,
	itemsPerPage,
	editing,
	setEditing,
	onPageChange,
	setTotalOpenBalance,
	setErrorValue,
	errorValue,
	setErrors,
	setHasValidationErrors,
	setPagesWithErrors,
	uploadMessageExcel,
	setUploadMessageExcel,
	isParentGlValues,
	checkViolations, // Add this prop
}) => {
	const toast = useToast();
	const [isLoading, setIsLoading] = useState(false);
	const [jumpPage, setJumpPage] = useState(1);
	const [inputValues, setInputValues] = useState({});
	const companyId = useSelector((state) => state.companyId);
	const userId = useSelector((state) => state.userId);
	const selectedLocation = useSelector((state) => state.selectedLocation);
	const selectedBranch = useSelector((state) => state.selectedBranch);
	const [totalPages, setTotalPages] = useState(1);
	const [rowValidationErrors, setRowValidationErrors] = useState([]);
	const startIndex = (currentPage - 1) * itemsPerPage;
	const endIndex = Math.min(startIndex + itemsPerPage, data.length);
	const currentData = data.slice(startIndex, endIndex);
	const { isSubmittedVendor, setIsSubmittedVendor } = useAdvances();

	const submitBtnRef = React.useRef(null);


	function toastCall(data) {
		return toast({
			title: data?.message,
			status: data?.status,
			duration: 3000,
			isClosable: true,
		});
	}

	function isValidNumber(input) {
		const numberPattern = /^-?[0-9]\d*(\.\d+)?$/;
		return numberPattern.test(input);
	}

	function isValidDate(input) {
		const datePattern = /^\d{2}-\d{2}-\d{4}$/;
		return datePattern.test(input);
	}

	function isValidText(input) {
		const textPattern = /^[A-Za-z0-9\s!@#$%^&*()_+{}\[\]:;<>,.?~\\/'/-]+$/;
		return textPattern.test(input);
	}

	function isValidVarchar(input) {
		const textPattern = /^(\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}[A-Z]{1}[A-Z\d]{1})?$/;
		return textPattern.test(input);
	}

	function isValidGSTIN(input) {
		if (!input || input.trim() === '') {
			return true;
		}
		const gstinPattern = /^[0-9]{2}(?:[A-Z]{5}[0-9]{4}[A-Z]|[A-Z]{4}[0-9]{5}[A-Z])[A-Z0-9]{1}[A-Z]{1}[A-Z0-9]{1}$/;
		return gstinPattern.test(input);
	}

	useEffect(() => {
		const newTotalPages = Math.ceil(data.length / itemsPerPage);
		setTotalPages(newTotalPages);
	}, [data, itemsPerPage]);

	useEffect(() => {
		if (editing) {
			const initialInputValues = {};
			const startIndex = (currentPage - 1) * itemsPerPage;
			const endIndex = Math.min(startIndex + itemsPerPage, data.length);
			const currentData = data.slice(startIndex, endIndex);

			currentData.forEach((row, rowIndex) => {
				Object.keys(row).forEach((header) => {
					initialInputValues[`${rowIndex}-${header}`] = row[header];
				});
			});

			setInputValues(initialInputValues);
		} else {
			setInputValues({});
		}
	}, [editing, currentPage, data, itemsPerPage]);

	useEffect(() => {
		const currentData = data.slice(startIndex, endIndex);
		const newErrors = currentData.map(() => ({}));

		currentData.forEach((row, rowIndex) => {
			Object.keys(row).forEach((header) => {
				const newValue = inputValues[`${rowIndex}-${header}`] || row[header];
				let error = null;

				if (header === 'vendor_code') {
					if (!isValidNumber(newValue)) {
						error = 'Invalid number input';
					}
				} else if (header === 'vendor_gstin') {
					if (!isValidGSTIN(newValue)) {
						error = 'Invalid gstin format';
					}
				} else if (header === 'inv_date') {
					if (!isValidDate(newValue)) {
						error = 'Invalid date format (DD-MM-YYYY)';
					}
				} else if (header === 'inv_posting_date') {
					if (!isValidDate(newValue)) {
						error = 'Invalid date format (DD-MM-YYYY)';
					}
				} else if (header === 'inv_due_date') {
					if (!isValidDate(newValue)) {
						error = 'Invalid date format (DD-MM-YYYY)';
					}
				}
				else if (header === 'due_amt') { // ✅ NEW RULE
					const numericValue = parseFloat(newValue);
					if (isNaN(numericValue) || numericValue >= 0) {
						error = 'Only negative values allowed';
					}
				} else if (header === 'inv_total') { // ✅ NEW RULE
					const numericValue = parseFloat(newValue);
					if (isNaN(numericValue) || numericValue >= 0) {
						error = 'Only negative values allowed';
					}
				} else if (header === 'inv_sub_total') { // ✅ NEW RULE
					const numericValue = parseFloat(newValue);
					if (isNaN(numericValue) || numericValue >= 0) {
						error = 'Only negative values allowed';
					}
				} else if (header === 'cgst') { // allow new rule
					const numericValue = parseFloat(newValue);
					if (isNaN(numericValue) || numericValue > 0) {
						error = 'Only negative values or 0 are allowed';
					}
				}
				else if (header === 'igst') { // ✅ NEW RULE
					const numericValue = parseFloat(newValue);
					if (isNaN(numericValue) || numericValue >= 0) {
						error = 'Only negative values allowed';
					}
				} else if (header === 'sgst') { // ✅ NEW RULE
					const numericValue = parseFloat(newValue);
					if (isNaN(numericValue) || numericValue >= 0) {
						error = 'Only negative values allowed';
					}
				} else if (header === 'tds') { // ✅ NEW RULE
					const numericValue = parseFloat(newValue);
					if (isNaN(numericValue) || numericValue >= 0) {
						error = 'Only negative values allowed';
					}
				}
				else {
					if (!isValidText(newValue)) {
						error = 'Invalid text input';
					}
				}
				newErrors[rowIndex][header] = error;
			});
		});

		setRowValidationErrors(newErrors);
	}, [currentPage, data, inputValues, startIndex, endIndex]);

	function calculateTotalOpenBalance() {
		const updatedTotalOpenBalance = data.reduce((total, row) => {
			const openBalanceValue = parseFloat(row['due_amt']);
			return isNaN(openBalanceValue) ? total : total + openBalanceValue;
		}, 0);

		setTotalOpenBalance(updatedTotalOpenBalance);
	}

	const handleEdit = useCallback(() => {
		if (!editing) {
			const newErrors = data.map(() => ({}));
			const pageErrorsMap = new Map();
			let invalidCount = 0;
			let totalOpenBalanceValue = 0;

			const updatedData = data.map((row, rowIndex) => {
				const updatedRow = { ...row };
				const rowErrors = {};

				Object.keys(row).forEach((header) => {
					const newValue = inputValues[`${rowIndex}-${header}`] || row[header];
					let error = null;

					if (header === 'customer_code') {
						if (!isValidNumber(newValue)) {
							error = 'Invalid number input';
							invalidCount++;
						}
					} else if (header === 'customer_gstin') {
						if (!isValidVarchar(newValue)) {
							error = 'Invalid VarChar format';
							invalidCount++;
						}
					} else if (header === 'inv_date') {
						if (!isValidDate(newValue)) {
							error = 'Invalid date format (DD-MM-YYYY)';
							invalidCount++;
						}
					} else if (header === 'inv_posting_date') {
						if (!isValidDate(newValue)) {
							error = 'Invalid date format (DD-MM-YYYY)';
						}
					} else if (header === 'inv_due_date') {
						if (!isValidDate(newValue)) {
							error = 'Invalid date format (DD-MM-YYYY)';
						}
					}
					else if (header === 'due_amt') {
						if (newValue !== '' && newValue !== ' ' && newValue !== null && !isValidNumber(newValue)) {
							error = 'Invalid number input';
							invalidCount++;
						}
					} else {
						if (!isValidText(newValue)) {
							error = 'Invalid text input';
							invalidCount++;
						}
					}

					rowErrors[header] = error;
					updatedRow[header] = newValue;
				});

				newErrors[rowIndex] = rowErrors;
				if (Object.values(rowErrors).some((err) => err !== null)) {
					const pageNum = Math.ceil((rowIndex + 1) / itemsPerPage);
					pageErrorsMap.set(pageNum, true);
				}
				const openBalanceValue = parseFloat(updatedRow['due_amt']);
				if (!isNaN(openBalanceValue)) {
					totalOpenBalanceValue += openBalanceValue;
				}

				return updatedRow;
			});

			setData(updatedData);
			setHasValidationErrors(invalidCount > 0);
			setErrors(newErrors);
			setErrorValue(invalidCount);
			setRowValidationErrors(newErrors);
			setPagesWithErrors(Array.from(pageErrorsMap.keys()));
			setTotalOpenBalance(totalOpenBalanceValue);

			const initialInputValues = {};
			currentData.forEach((row, rowIndex) => {
				Object.keys(row).forEach((header) => {
					initialInputValues[`${rowIndex}-${header}`] = row[header];
				});
			});
			setInputValues(initialInputValues);

			// Check for violations after validation
			checkViolations(updatedData);
		} else {
			setHasValidationErrors(false);
			setRowValidationErrors([]);
			setInputValues({});
		}

		setEditing(!editing);
	}, [
		editing,
		data,
		inputValues,
		setTotalOpenBalance,
		setErrorValue,
		setData,
		currentData,
		itemsPerPage,
		checkViolations,
	]);

	useEffect(() => {
		if (isSubmittedVendor && submitBtnRef.current) {
			submitBtnRef.current.click();
			setIsSubmittedVendor(false); // reset after clicking
		}
	}, [isSubmittedVendor, setIsSubmittedVendor]);

	const handleDeleteRow = (rowIndex) => {
		const newData = [...data];
		newData.splice(startIndex + rowIndex, 1);
		setData(newData);
	};

	const handleInputChange = (rowIndex, header, newValue) => {
		let error = null;
		if (header === 'customer_code') {
			if (!isValidNumber(newValue)) {
				error = 'Invalid number input';
			}
		} else if (header === 'vendor_gstin') {
			if (!isValidGSTIN(newValue)) {
				error = 'Invalid gstin format';
			}
		} else if (header === 'due_amt') {
			if (newValue !== '' && newValue !== ' ' && newValue !== null && !isValidNumber(newValue)) {
				error = 'Invalid number input';
			} else {
				const updatedData = [...data];
				updatedData[startIndex + rowIndex][header] = newValue;
				setData(updatedData);
			}
		} else {
			if (newValue === '' || newValue === ' ') {
				error = null;
			} else if (!isValidText(newValue)) {
				error = 'Invalid text input';
			}
		}

		setInputValues((prevInputValues) => ({
			...prevInputValues,
			[`${rowIndex}-${header}`]: newValue,
		}));

		if (!error) {
			const updatedData = [...data];
			updatedData[startIndex + rowIndex][header] = newValue;
			setData(updatedData);
		}

		calculateTotalOpenBalance();
	};

	const handleJumpPageChange = (event) => {
		const value = event.target.value;
		setJumpPage(value);
	};

	const jumpToPage = () => {
		const pageNumber = parseInt(jumpPage, 10);
		if (isNaN(pageNumber) || pageNumber < 1 || pageNumber > totalPages) {
			toast({
				title: 'Invalid page number',
				status: 'error',
				duration: 3000,
				isClosable: true,
			});
			return;
		}

		onPageChange(pageNumber);
	};

	const newData = data.map((item) => {
		const newItem = {
			subgl: `${item.vendor_code}`,
			inv_no: `${item.inv_no}`,
			inv_date: `${item.inv_date}`,
			inv_posting_date: `${item.inv_posting_date}`,//new
			inv_due_date: `${item.inv_due_date}`,//new
			inv_total: `${item.inv_total}`,
			due_amt: item.due_amt,
			inv_sub_total: `${item.inv_sub_total}`,
			cgst: item.cgst,
			igst: item.igst,
			sgst: item.sgst,
			tds: item.tds,
			// credit_period: `${item.credit_period}`,
			// sub_total: item.sub_total,
			// conversion_rate: item.conversion_rate,
			// currency_name: item.currency_name,
			// total_amt: item.total_amt,
			// customer_billing_address: item.customer_billing_address,
			// customer_shipping_address: item.customer_shipping_address,

		};

		return newItem;
	});

	const uploadItem = async (e) => {
		e.preventDefault();
		let formData = new FormData();
		formData.append('company_id', companyId);
		formData.append('location_id', selectedLocation);
		formData.append('branch_id', selectedBranch);
		formData.append('user_id', userId);
		formData.append('data', JSON.stringify(newData));

		try {
			setIsLoading(true);
			const response = await fetch(
				`${process.env.REACT_APP_API_URL}vendor-invoice.php`,
				{
					method: 'POST',
					body: formData,
				}
			);

			if (response.ok) {
				setIsLoading(false);
				const responseData = await response.json();
				toastCall(responseData);
				setUploadMessageExcel(responseData.data);
				setData([]);
				setEditing(false);
			} else {
				setIsLoading(false);
				const responseData = await response.json();
				toastCall(responseData);
				setUploadMessageExcel(responseData.data);
				setEditing(false);
			}
		} catch (error) {
			setIsLoading(false);
			setEditing(false);
		}
	};

	function generateCSVData(demoXlSX) {
		const headers = Object.keys(demoXlSX[0]);
		const headerRow = headers.join(',') + '\n';
		const rows = demoXlSX
			.map((item) => {
				const values = headers.map((header) => item[header]);
				return values.join(',');
			})
			.join('\n');

		return headerRow + rows;
	}

	const uploadMessageExcelDownload = () => {
		if (!uploadMessageExcel || uploadMessageExcel.length === 0) {
			alert('No data to download.');
			return;
		}
		const uploadedData = uploadMessageExcel?.map((items) => ({
			...items,
		}));
		const csvData = generateCSVData(uploadedData);
		const blob = new Blob([csvData], {
			type: 'text/csv;charset=utf-8',
		});
		saveAs(blob, `Vendor-Balance-Upload-Log.csv`);
	};

	return (
		<>
			<form style={{ width: '100%' }} onSubmit={uploadItem}>
				<Box width="100%" mt="50px">
					{currentData?.length > 0 && (
						<>
							<Box display="flex" alignItems="flex-end" justifyContent="flex-end" mb="15px">
								<Button
									borderRadius="10px"
									border="1px solid #003060"
									p="20px 30px"
									fontSize="15px"
									background="none"
									color="mainBlueColor"
									onClick={handleEdit}
								>
									{editing ? 'Save' : 'Validate'}
								</Button>
							</Box>

							<Box
								width="100%"
								overflowX="scroll"
								maxH="500px"
								sx={{
									'& .table_bg': {
										color: 'black',
										textAlign: 'center',
									},
									'& .table_bg:first-of-type': {
										textTransform: 'uppercase',
										textAlign: 'center',
										minHeight: '50px',
										backgroundColor: `${editing ? 'red' : 'var(--chakra-colors-tableSkyBlueLightColor)'}`,
										fontWeight: '600',
										color: 'var(--chakra-colors-mainBlueColor)',
									},
									'& .table_bg input': {
										textAlign: 'center',
										fontSize: '14px',
										border: '1px solid var(--chakra-colors-textLightGrayColor)',
										height: '40px',
									},
									'& .table_bg:first-of-type input': {
										textTransform: 'uppercase',
										textAlign: 'center',
										minHeight: '50px',
										backgroundColor: `${editing ? 'var(--chakra-colors-mainBlueColor)' : 'var(--chakra-colors-tableSkyBlueLightColor)'}`,
										fontWeight: '600',
										color: 'white',
										border: 'none',
										pointerEvents: 'none',
									},
								}}
							>
								<Box className="table_bg" display="flex">
									{Object.keys(currentData[0]).map((header) => (
										<Box
											key={header}
											display="flex"
											alignItems="center"
											justifyContent="center"
											border="1px solid #ccc"
											padding={editing ? '5px' : '10px'}
											minW="400px"
											backgroundColor={editing ? 'white' : 'var(--chakra-colors-tableSkyBlueLightColor)'}
										>
											{header}
										</Box>
									))}
								</Box>

								{currentData.map((row, rowIndex) => (
									<Box className="table_bg" key={rowIndex} style={{ display: 'flex', marginBottom: '10px' }}>
										{Object.keys(row).map((header) => (
											<Box
												key={header}
												display="flex"
												flexDirection="column"
												alignItems="center"
												justifyContent="center"
												border="1px solid #ccc"
												padding={editing ? '5px' : '10px'}
												minWidth="400px"
												backgroundColor={editing ? 'white' : 'var(--chakra-colors-tableSkyBlueLightColor)'}
											>
												{editing ? (
													<>
														<Input
															className="input_box"
															type="text"
															value={inputValues[`${rowIndex}-${header}`] || row[header]}
															onChange={(e) => handleInputChange(rowIndex, header, e.target.value)}
														/>
														{rowValidationErrors[rowIndex] && rowValidationErrors[rowIndex][header] && (
															<Box fontSize="1.2rem" fontWeight="600" color="red">
																{rowValidationErrors[rowIndex][header]}
															</Box>
														)}
													</>
												) : (
													row[header]
												)}
											</Box>
										))}
										<Box display="flex" alignItems="center" justifyContent="center" minWidth="50px">
											<Button
												size="sm"
												background="none"
												_hover={{ bg: 'none' }}
												_active={{ bg: 'none' }}
												_focus={{ bg: 'none' }}
												onClick={() => handleDeleteRow(rowIndex)}
											>
												<i className="fa-solid fa-trash fa-2x"></i>
											</Button>
										</Box>
									</Box>
								))}
							</Box>
						</>
					)}
				</Box>

				{currentData?.length > 0 && (
					<Box
						width="100%"
						display="flex"
						alignItems="center"
						justifyContent="space-between"
						mt="15px"
					>
						<Box width="100%" display="flex" alignItems="center" gap="15px">
							<Button
								onClick={() => onPageChange(currentPage - 1)}
								isDisabled={currentPage === 1 ? true : false}
								fontSize="14px"
								p="10px"
								bg="var(--chakra-colors-mainBlueColor)"
								color="white"
								_hover={{ bg: 'rgba(0,67,135,1)', color: 'white' }}
								_active={{ bg: 'rgba(0,67,135,1)', color: 'white' }}
							>
								Previous
							</Button>
							<Box as="span">
								Page No {currentPage} Out of {totalPages}
							</Box>
							<Button
								onClick={() => {
									if (currentPage < totalPages) {
										onPageChange(currentPage + 1);
									} else {
										toast({
											title: 'No more data available',
											status: 'warning',
											duration: 3000,
											isClosable: true,
										});
									}
								}}
								disabled={endIndex >= data.length}
								fontSize="14px"
								p="10px"
								bg="var(--chakra-colors-mainBlueColor)"
								color="white"
								_hover={{ bg: 'rgba(0,67,135,1)', color: 'white' }}
								_active={{ bg: 'rgba(0,67,135,1)', color: 'white' }}
							>
								Next
							</Button>
							<Box display="flex" alignItems="center">
								<Input
									type="number"
									min="1"
									max={totalPages}
									value={jumpPage}
									onChange={handleJumpPageChange}
									width="60px"
									mr="10px"
								/>
								<Button
									onClick={jumpToPage}
									fontSize="14px"
									p="10px"
									bg="var(--chakra-colors-mainBlueColor)"
									color="white"
									_hover={{ bg: 'rgba(0,67,135,1)', color: 'white' }}
									_active={{ bg: 'rgba(0,67,135,1)', color: 'white' }}
								>
									Jump to Page
								</Button>
							</Box>
						</Box>
						<Box width="100%" display="flex" alignItems="center" justifyContent="flex-end">
							{editing && (
								<Button
									ref={submitBtnRef}
									isDisabled={!isParentGlValues || isLoading}
									isLoading={isLoading}
									m="10px 0px"
									bgGradient="linear(180deg, #2267A2 0%, #0D4675 100%)"
									boxShadow="0px 4px 4px rgba(0, 0, 0, 0.25)"
									borderRadius="10px"
									p="20px 40px"
									fontSize="1.6rem"
									color="white"
									type="submit"
									_hover={{ bgGradient: 'linear(180deg, #2267A2 0%, #0D4675 100%)' }}
									_active={{ bgGradient: 'linear(180deg, #2267A2 0%, #0D4675 100%)' }}
									_focus={{ bgGradient: 'linear(180deg, #2267A2 0%, #0D4675 100%)' }}
								>
									Submit
								</Button>
							)}
						</Box>
					</Box>
				)}
			</form>

			{uploadMessageExcel !== undefined && (
				<Box
					display="flex"
					justifyContent="space-between"
					alignItems="center"
					boxShadow="rgba(50, 50, 93, 0.25) 0px 2px 5px -1px, rgba(0, 0, 0, 0.3) 0px 1px 3px -1px"
					borderRadius="10px"
					bg="#dbe5ee"
					mt="20px"
					p="15px"
					w="100%"
				>
					<Box>
						<Text fontWeight="600" mb="10px">
							Successfully Uploaded :
						</Text>
						<Text fontWeight="600" mb="10px">
							Upload Failed :
						</Text>
					</Box>
					<Button
						onClick={uploadMessageExcelDownload}
						m="10px 0px"
						bgGradient="linear(180deg, #2267A2 0%, #0D4675 100%)"
						boxShadow="0px 4px 4px rgba(0, 0, 0, 0.25)"
						borderRadius="10px"
						p="20px 40px"
						fontSize="1.6rem"
						color="white"
						_hover={{ bgGradient: 'linear(180deg, #2267A2 0%, #0D4675 100%)' }}
						_active={{ bgGradient: 'linear(180deg, #2267A2 0%, #0D4675 100%)' }}
						_focus={{ bgGradient: 'linear(180deg, #2267A2 0%, #0D4675 100%)' }}
					>
						<i className="fa-solid fa-circle-down"></i>{' '}
						<Box as="span" ml="10px">
							Download Log
						</Box>
					</Button>
				</Box>
			)}
		</>
	);
};

export default VendorInvoicePaginatedData;
